/**
 * Shared GraphQL Fragments
 *
 * Reusable fragments for common field selections across queries.
 * These fragments reduce duplication and ensure consistency.
 *
 * Feature: 063-graphql-frontend
 */

import { gql } from '@apollo/client';

/**
 * Basic user fields for display in lists and comments
 */
export const USER_BASIC_FRAGMENT = gql`
  fragment UserBasic on User {
    id
    globalId
    username
    displayName
  }
`;

/**
 * Photo thumbnail fields for lists and previews
 */
export const PHOTO_THUMBNAIL_FRAGMENT = gql`
  fragment PhotoThumbnail on Photo {
    id
    globalId
    title
    userId
    metadata {
      width
      height
    }
    variants {
      id
      variantType
      storageUrl
      publicUrl
      width
      height
    }
  }
`;

/**
 * Full photo metadata for detail views
 */
export const PHOTO_METADATA_FRAGMENT = gql`
  fragment PhotoMetadata on PhotoMetadata {
    width
    height
    format
    fileSize
    camera
    lens
    iso
    aperture
    shutterSpeed
    focalLength
    dateTaken
    latitude
    longitude
  }
`;

/**
 * Basic critique fields for lists
 */
export const CRITIQUE_BASIC_FRAGMENT = gql`
  fragment CritiqueBasic on Critique {
    id
    globalId
    content
    photoId
    parentId
    editId
    createdAt
    updatedAt
    userId
  }
`;

/**
 * Edit suggestion fields
 */
export const EDIT_SUGGESTION_FRAGMENT = gql`
  fragment EditSuggestion on CritiqueEdit {
    id
    globalId
    critiqueId
    parentEditId
    editType
    edits
    createdAt
    updatedAt
  }
`;

/**
 * Collection summary fields for lists
 */
export const COLLECTION_SUMMARY_FRAGMENT = gql`
  fragment CollectionSummary on Collection {
    id
    globalId
    title
    slug
    description
    layout
    createdAt
    updatedAt
    heroPhotoId
  }
`;

/**
 * Collection item fields
 */
export const COLLECTION_ITEM_FRAGMENT = gql`
  fragment CollectionItem on CollectionItem {
    id
    globalId
    photoId
    position
    caption
    layout
  }
`;

/**
 * Critique session summary fields
 */
export const CRITIQUE_SESSION_SUMMARY_FRAGMENT = gql`
  fragment CritiqueSessionSummary on CritiqueSession {
    id
    globalId
    title
    collectionId
    createdAt
    updatedAt
  }
`;

/**
 * Page info fields for pagination
 */
export const PAGE_INFO_FRAGMENT = gql`
  fragment PageInfo on PageInfo {
    hasNextPage
    hasPreviousPage
  }
`;
